/**
 * ActivityAuditPage — main Activity Audit hub.
 *
 * Route: /#/activity/audit
 *
 * LOCKED DECISIONS:
 *   A8  Tab within BDR/AE/Manager dashboard (this is the tabbed page that
 *       renders the appropriate role view based on the active role).
 *   A5  Executive view = zero individual attribution.
 *   A3  Marketing = "Coming soon" placeholder.
 *
 * The role is determined from the URL param ?role= or falls back to the
 * session's active role. A role-tab selector sits at the top of the page
 * (accessible to Manager and Executive who use the role switcher).
 *
 * TENANT ISOLATION: All sub-views enforce tenant_id != 2.
 */

import { useState } from "react";
import { useLocation } from "wouter";
import OperatorShell from "@/components/operator/OperatorShell";
import { tokens } from "@/components/operator/tokens";
import ActivityAuditBdr from "./ActivityAuditBdr";
import ActivityAuditAe from "./ActivityAuditAe";
import ActivityAuditManager from "./ActivityAuditManager";
import ActivityAuditExecutive from "./ActivityAuditExecutive";
import ActivityAuditMarketing from "./ActivityAuditMarketing";
import { getSession } from "@/lib/session";

type AuditRole = "bdr" | "ae" | "manager" | "executive" | "marketing";

const ROLE_TABS: { key: AuditRole; label: string }[] = [
  { key: "bdr",       label: "BDR" },
  { key: "ae",        label: "AE" },
  { key: "manager",   label: "Manager" },
  { key: "executive", label: "Executive" },
  { key: "marketing", label: "Marketing" },
];

// Resolve initial role from URL param or session
function resolveInitialRole(): AuditRole {
  if (typeof window !== "undefined") {
    const hash = window.location.hash;
    const roleParam = hash.match(/[?&]role=([^&]+)/)?.[1];
    if (roleParam && ROLE_TABS.some((t) => t.key === roleParam)) {
      return roleParam as AuditRole;
    }
  }
  const session = getSession();
  const sessionRole = session?.role;
  if (sessionRole && ROLE_TABS.some((t) => t.key === sessionRole)) {
    return sessionRole as AuditRole;
  }
  return "bdr";
}

export default function ActivityAuditPage() {
  const [activeRole, setActiveRole] = useState<AuditRole>(resolveInitialRole);
  const [, setLocation] = useLocation();

  function handleRoleChange(role: AuditRole) {
    setActiveRole(role);
    // Update URL so the view is bookmarkable
    const currentHash = window.location.hash.split("?")[0];
    const newHash = `${currentHash}?role=${role}`;
    window.history.replaceState(null, "", `#${newHash.replace(/^#/, "")}`);
  }

  const session = getSession();
  const sessionRole = session?.role ?? "manager";

  // Role access: Manager/Executive/admin can switch; others see only their own
  const canSwitchRoles = ["manager", "executive"].includes(sessionRole) || !session;
  const visibleTabs = canSwitchRoles
    ? ROLE_TABS
    : ROLE_TABS.filter((t) => t.key === sessionRole);

  return (
    <OperatorShell
      breadcrumbs={[
        { label: "Home", href: "/" },
        { label: "Dashboards", href: `/dashboards/${sessionRole}` },
        { label: "Activity Audit" },
      ]}
      primaryAction={
        activeRole === "bdr"
          ? { label: "+ Log Activity", href: "/log/bdr" }
          : activeRole === "ae"
            ? { label: "+ Log Activity", href: "/log/ae" }
            : undefined
      }
      pageTitle="Activity Audit"
      pageMeta="What did we actually do, what moved, and what did not."
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
        {/* Role tab bar (A8: tab within dashboard) */}
        <div
          style={{
            display: "flex",
            gap: 0,
            borderBottom: `1px solid ${tokens.color.border}`,
            marginBottom: 24,
            overflowX: "auto",
          }}
        >
          {visibleTabs.map(({ key, label }) => {
            const isActive = key === activeRole;
            return (
              <button
                key={key}
                type="button"
                onClick={() => handleRoleChange(key)}
                style={{
                  padding: "10px 20px",
                  border: "none",
                  borderBottom: isActive
                    ? `2px solid ${tokens.color.red}`
                    : "2px solid transparent",
                  background: "transparent",
                  color: isActive ? tokens.color.textPrimary : tokens.color.textMuted,
                  fontSize: 13,
                  fontWeight: isActive ? 700 : 400,
                  fontFamily: tokens.font.body,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  transition: "color 0.15s, border-color 0.15s",
                  marginBottom: -1,
                }}
                onMouseEnter={(e) => {
                  if (!isActive) (e.currentTarget as HTMLButtonElement).style.color = tokens.color.textPrimary;
                }}
                onMouseLeave={(e) => {
                  if (!isActive) (e.currentTarget as HTMLButtonElement).style.color = tokens.color.textMuted;
                }}
              >
                {label}
                {key === "marketing" && (
                  <span
                    style={{
                      marginLeft: 6, fontSize: 9, fontWeight: 700,
                      letterSpacing: "0.08em", textTransform: "uppercase",
                      padding: "1px 5px", borderRadius: 99,
                      background: "#EFEFEE", color: "#8E8E94",
                    }}
                  >
                    Soon
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Role view content */}
        <div>
          {activeRole === "bdr"       && <ActivityAuditBdr />}
          {activeRole === "ae"        && <ActivityAuditAe />}
          {activeRole === "manager"   && <ActivityAuditManager />}
          {activeRole === "executive" && <ActivityAuditExecutive />}
          {activeRole === "marketing" && <ActivityAuditMarketing />}
        </div>
      </div>
    </OperatorShell>
  );
}
